import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { pointsApi } from "@api";
import { IPointsData, IPointsHistoryItem, ICommissionHistoryItem } from "@interfaces";
import { RootState } from ".";

interface PointsState {
    data: IPointsData | null;
    history: IPointsHistoryItem[];
    commissionHistory: ICommissionHistoryItem[];
    loading: boolean;
    refreshing: boolean;
    error: string | null;
    historyLoading: boolean;
    historyError: string | null;
}

const initialState: PointsState = {
    data: null,
    history: [],
    commissionHistory: [],
    loading: true,
    refreshing: false,
    error: null,
    historyLoading: false,
    historyError: null,
};

export const fetchPoints = createAsyncThunk(
    "points/fetchPoints",
    async (_, { rejectWithValue }) => {
        try {
            const res = await pointsApi.getPointsData();
            return res.data.data as IPointsData;
        } catch (err: any) {
            return rejectWithValue(err.response?.data || err.message);
        }
    },
);

export const fetchPointsHistory = createAsyncThunk(
    "points/fetchPointsHistory",
    async (
        params: {
            startDate?: string;
            endDate?: string;
            untilTodayOnly: "1" | "0";
            onlyAvailablePoints: "1" | "0";
        },
        thunkAPI,
    ) => {
        try {
            const response = await pointsApi.getPointsHistory(params);
            return {
                cashback: response.data.data.cashback as IPointsHistoryItem[],
                commission: response.data.data.commission as ICommissionHistoryItem[],
            };
        } catch (err: any) {
            return thunkAPI.rejectWithValue(err.message);
        }
    },
);

const pointsSlice = createSlice({
    name: "points",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        // --- Points data ---
        builder
            .addCase(fetchPoints.pending, (state) => {
                if (!state.data) {
                    state.loading = true;
                } else {
                    state.refreshing = true;
                }
                state.error = null;
            })
            .addCase(fetchPoints.fulfilled, (state, action) => {
                state.data = action.payload;
                state.loading = false;
                state.refreshing = false;
            })
            .addCase(fetchPoints.rejected, (state, action) => {
                state.loading = false;
                state.refreshing = false;
                state.error = action.payload as string;
            });

        // --- Points history ---
        builder
            .addCase(fetchPointsHistory.pending, (state) => {
                state.historyLoading = true;
                state.historyError = null;
            })
            .addCase(
                fetchPointsHistory.fulfilled,
                (
                    state,
                    action: PayloadAction<{
                        cashback: IPointsHistoryItem[];
                        commission: ICommissionHistoryItem[];
                    }>,
                ) => {
                    state.historyLoading = false;
                    state.history = action.payload.cashback || [];
                    state.commissionHistory = action.payload.commission || [];
                },
            )
            .addCase(fetchPointsHistory.rejected, (state, action) => {
                state.historyLoading = false;
                state.historyError = action.payload as string;
            });
    },
});

export default pointsSlice.reducer;
export const selectPoints = (state: RootState) => state.points;
